'use client';

import { useState, useEffect } from 'react';
import { Trip, getTrips, TripsListResponse } from '@/lib/trips/api';
import TripCard from './TripCard';
import { cn } from '@/lib/utils';

interface TripResultsGridProps {
  onSelectTrip?: (trip: Trip) => void;
  selectedTripId?: string;
  searchParams?: {
    destination?: string;
    startDate?: string;
    duration?: number;
    tripType?: 'Join-In' | 'Private';
    adults?: number;
    children?: number;
  };
  className?: string;
}

export default function TripResultsGrid({
  onSelectTrip,
  selectedTripId,
  searchParams,
  className,
}: TripResultsGridProps) {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    fetchTrips();
  }, [page, searchParams]);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response: TripsListResponse = await getTrips(
        page,
        12, // Show 12 trips per page for grid layout
        'created_at',
        'DESC',
        searchParams?.destination || ''
      );

      if (response) {
        let filteredTrips = response.data || [];

        // Apply client-side filters based on searchParams
        if (searchParams?.tripType) {
          filteredTrips = filteredTrips.filter(
            (trip) => trip.trip_type === searchParams.tripType
          );
        }

        if (searchParams?.duration) {
          filteredTrips = filteredTrips.filter(
            (trip) => trip.days <= searchParams.duration!
          );
        }

        setTrips(filteredTrips);
        setTotalPages(response.meta.total_pages);
        setTotalRecords(response.meta.total_records);
      }
    } catch (err) {
      console.error('Error fetching trips:', err);
      setError('Failed to load trips. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrip = (trip: Trip) => {
    onSelectTrip?.(trip);
  };

  if (loading) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-dark dark:text-white">
            Available Trips
          </h3>
        </div>
        
        {/* Loading Skeleton */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse rounded-xl bg-white shadow-card dark:bg-dark-2"
            >
              <div className="h-48 bg-gray-200 dark:bg-dark-3" />
              <div className="p-4 space-y-3">
                <div className="h-5 w-3/4 rounded bg-gray-200 dark:bg-dark-3" />
                <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-dark-3" />
                <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-dark-3" />
                <div className="flex items-center justify-between pt-3">
                  <div className="h-6 w-20 rounded bg-gray-200 dark:bg-dark-3" />
                  <div className="h-9 w-20 rounded bg-gray-200 dark:bg-dark-3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-dark dark:text-white">
            Available Trips
          </h3>
        </div>
        
        <div className="flex flex-col items-center justify-center rounded-xl bg-red-50 py-12 dark:bg-red-900/10">
          <svg
            className="h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchTrips}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-dark dark:text-white">
            Available Trips
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {totalRecords > 0
              ? `Found ${totalRecords} trip${totalRecords !== 1 ? 's' : ''}`
              : 'No trips found'}
          </p>
        </div>
        
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-stroke px-3 py-1.5 text-sm font-medium text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
            >
              ← Prev
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-stroke px-3 py-1.5 text-sm font-medium text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Results Grid */}
      {trips.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {trips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              onSelect={handleSelectTrip}
              selected={trip.id === selectedTripId}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl bg-gray-2 py-12 dark:bg-dark-2">
          <svg
            className="h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            No trips found matching your criteria
          </p>
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
            Try adjusting your search filters
          </p>
        </div>
      )}

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center pt-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-stroke px-4 py-2 text-sm font-medium text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
            >
              ← Previous
            </button>
            <span className="px-4 text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-stroke px-4 py-2 text-sm font-medium text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
